/**
 * Version history for one article — Step 122.
 *
 * A list of every save that changed the content, newest first, and for the
 * selected one a word-level diff against what the article says NOW. The
 * reference hides its history until an article has been "updated more than
 * once" and shows no diff — a list of dates is not an audit trail. Restore
 * makes a new version from the old one; nothing is ever deleted.
 */
import { useMemo } from 'react';
import { useUrlState } from '../../lib/urlState';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { kbApi, type KbArticleDetail } from './api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { useConfirm } from '../../lib/confirm';
import { wordDiff, diffSummary } from '../../lib/wordDiff';
import { cn } from '../../lib/utils';

/** Tags off, entities back, whitespace collapsed — enough to diff prose. */
function textOf(html: string | null | undefined): string {
  if (!html) return '';
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function VersionHistory({
  article,
  open,
  onClose,
  canRestore,
}: {
  article: KbArticleDetail;
  open: boolean;
  onClose: () => void;
  canRestore: boolean;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  // Which version is open lives in the URL (`?version=`) — a refresh keeps it
  // and a colleague can be sent straight to "look at v3 against current".
  const [selectedRaw, setSelectedRaw] = useUrlState('version', null);
  const selected = selectedRaw && /^\d+$/.test(selectedRaw) ? Number(selectedRaw) : null;
  const setSelected = (v: number | null) => setSelectedRaw(v === null ? null : String(v));
  const versions = useQuery({
    queryKey: qk.kbVersions(article.id),
    queryFn: () => kbApi.versions(article.id),
    enabled: open,
  });
  const chosen = useQuery({
    queryKey: qk.kbVersion(article.id, selected ?? -1),
    queryFn: () => kbApi.version(article.id, selected!),
    enabled: open && selected !== null,
  });
  const current = useMemo(
    () => `${article.title}\n${textOf(article.bodyHtml)}`,
    [article.title, article.bodyHtml],
  );
  const ops = useMemo(
    () => (chosen.data ? wordDiff(`${chosen.data.title}\n${textOf(chosen.data.bodyHtml)}`, current) : null),
    [chosen.data, current],
  );
  const summary = ops ? diffSummary(ops) : null;
  const restore = useMutation({
    mutationFn: (v: number) => kbApi.restoreVersion(article.id, v),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      toast.success(
        `Restored as version ${res.body?.version ?? ''}. The content it replaced is still in the history.`,
      );
      void qc.invalidateQueries({ queryKey: qk.kbArticle(article.id) });
      void qc.invalidateQueries({ queryKey: qk.kbVersions(article.id) });
      setSelected(null);
    },
  });
  const latest = versions.data?.entries[0]?.version ?? null;
  const close = () => {
    setSelected(null);
    onClose();
  };

  return (
    <Sheet open={open} onClose={close} title="Version history" wide>
      <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row">
        <ol className="md:w-64 md:shrink-0 md:overflow-y-auto">
          {versions.isPending && <Skeleton className="h-32" />}
          {versions.data?.entries.map((v) => (
            <li key={v.version}>
              <button
                onClick={() => setSelected(v.version === latest ? null : v.version)}
                className={cn(
                  'w-full rounded-md px-2.5 py-2 text-left text-xs hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selected === v.version && 'bg-accent',
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium">v{v.version}</span>
                  {v.version === latest && <Badge variant="secondary">current</Badge>}
                  {v.note && v.note !== 'edited' && <span className="text-muted-foreground">{v.note}</span>}
                </span>
                <span className="mt-0.5 block text-muted-foreground">
                  {fullTime(v.createdAt)}
                  {v.savedByName ? ` · ${v.savedByName}` : ''}
                </span>
                <span className="block truncate text-muted-foreground">{v.title}</span>
              </button>
            </li>
          ))}
          {versions.data && versions.data.entries.length === 0 && (
            <p className="p-2 text-xs text-muted-foreground">No versions yet.</p>
          )}
        </ol>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {selected === null && (
            <p className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
              <History className="mx-auto mb-2 h-5 w-5" />
              Pick an earlier version to see what has changed since — words removed are struck through, words
              added are highlighted.
            </p>
          )}
          {selected !== null && chosen.isPending && <Skeleton className="h-48" />}
          {selected !== null && ops && summary && (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium">v{selected} → current</span>
                <span className="text-emerald-700 dark:text-emerald-300">+{summary.added} words</span>
                <span className="text-destructive">−{summary.removed} words</span>
                <span className="flex-1" />
                {canRestore && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={restore.isPending}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Restore version ${selected}?`,
                          body: 'The current content becomes the previous version — nothing is lost, and you can restore forwards again.',
                          confirmLabel: 'Restore',
                        })
                      )
                        restore.mutate(selected);
                    }}
                  >
                    <RotateCcw /> Restore this version
                  </Button>
                )}
              </div>
              <p className="whitespace-pre-wrap rounded-md border p-3 text-sm leading-relaxed">
                {ops.map((o, i) =>
                  o.kind === 'same' ? (
                    <span key={i}>{o.text}</span>
                  ) : o.kind === 'added' ? (
                    <mark key={i} className="rounded bg-emerald-500/20 text-foreground">
                      {o.text}
                    </mark>
                  ) : (
                    <del key={i} className="rounded bg-destructive/15 text-destructive">
                      {o.text}
                    </del>
                  ),
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </Sheet>
  );
}
